import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { App, Button, Form, Input, Modal, Space, Table } from 'antd'
import { brand } from '../../theme/brand'
import { ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { renderAvatarName } from '../../theme/columns'
import { supabaseClient } from '../../lib/supabaseClient'
import { groupsAdminErrorMessage } from '../groups/adminErrors'
import { categoryLabel } from '../groups/categoryOptions'
import { privacyLabel } from '../groups/list'
import { Clock } from '../../components/icons'

interface ProposalRow {
  id: string
  name: string
  tagline: string | null
  category: string
  privacy: string
  cover_url: string | null
  cover_moderation_status: string
  class_year_min: string | null
  class_year_max: string | null
  created_by: string | null
  created_at: string
  proposer_name: string
  proposer_class_year: string | null
  similar?: { slug: string; name: string } | null
}

/**
 * Group proposals — the approval queue for member-proposed groups
 * (`groups` where `status = pending_review`). Direct supabaseClient reads
 * (not Refine's useTable), same reasoning as GroupMembersTable: `created_by`
 * points at auth.users, not profiles, so the proposer's name needs a second
 * query merged in, exactly like that table's member roster.
 *
 * "Checks" pills (COMPONENTS-COPY §"Admin (Refine) copy" — Proposals page):
 *   Cover OK / Cover needs review   — from cover_moderation_status
 *   Similar: {name}                 — similar_group_names(name), best match
 *   Limit to class ’{yy}?           — a narrow class_year_min=class_year_max
 *                                      hints this might really be a class-year
 *                                      group rather than an open interest one
 */
export function GroupProposalsList() {
  const { message } = App.useApp()
  const [rows, setRows] = useState<ProposalRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [declineTarget, setDeclineTarget] = useState<ProposalRow | null>(null)
  const [declineForm] = Form.useForm<{ note: string }>()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data: groups, error } = await supabaseClient
        .from('groups')
        .select(
          'id, name, tagline, category, privacy, cover_url, cover_moderation_status, class_year_min, class_year_max, created_by, created_at',
        )
        .eq('kind', 'interest')
        .eq('status', 'pending_review')
        .order('created_at', { ascending: false })
      if (error) throw error

      const ids = (groups ?? []).map((g) => g.created_by).filter((id): id is string => Boolean(id))
      const byId = new Map<string, { name: string; class_year: string | null }>()
      if (ids.length > 0) {
        const { data: profiles } = await supabaseClient
          .from('profiles')
          .select('user_id, name, class_year')
          .in('user_id', ids)
        for (const p of profiles ?? []) {
          if (p.user_id) byId.set(p.user_id, { name: p.name ?? 'Unknown member', class_year: p.class_year })
        }
      }

      const merged: ProposalRow[] = (groups ?? []).map((g) => ({
        ...g,
        proposer_name: (g.created_by && byId.get(g.created_by)?.name) || 'Unknown member',
        proposer_class_year: (g.created_by && byId.get(g.created_by)?.class_year) || null,
        similar: undefined,
      }))
      setRows(merged)

      // Best-effort similar-name check per row (admin bypasses groups_enabled
      // via similar_group_names' own _groups_assert_enabled() admin exemption).
      // Fire-and-forget per row so one failure never blocks the table.
      for (const row of merged) {
        supabaseClient.rpc('similar_group_names', { p_name: row.name }).then(
          ({ data }) => {
            const match = (data ?? []).find((s) => s.slug !== row.id)
            if (match) {
              setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, similar: match } : r)))
            }
          },
          () => {},
        )
      }
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }, [message])

  useEffect(() => {
    void load()
  }, [load])

  const stats: Stat[] = useMemo(
    () => [{ label: 'Pending', value: rows.length.toLocaleString(), icon: Clock, tone: rows.length > 0 ? 'gold' : 'neutral' }],
    [rows.length],
  )

  async function approve(row: ProposalRow) {
    setBusyId(row.id)
    try {
      const { error } = await supabaseClient.rpc('review_group', { p_group: row.id, p_approve: true })
      if (error) throw error
      message.success(`${row.name} is now live.`)
      setRows((prev) => prev.filter((r) => r.id !== row.id))
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setBusyId(null)
    }
  }

  async function decline(row: ProposalRow, note: string) {
    setBusyId(row.id)
    try {
      const { error } = await supabaseClient.rpc('review_group', { p_group: row.id, p_approve: false, p_note: note })
      if (error) throw error
      message.success(`${row.name} was declined.`)
      setRows((prev) => prev.filter((r) => r.id !== row.id))
      setDeclineTarget(null)
      declineForm.resetFields()
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Groups',
        description:
          'Groups members proposed while review is required (Groups Settings → "Review new member groups"). Approve to make a group live immediately, or decline with a note the proposer will see.',
      }}
      stats={stats}
      statColumns={1}
    >
      <TableSurface>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          scroll={{ x: true }}
          pagination={{ pageSize: 20 }}
        >
          <Table.Column<ProposalRow>
            dataIndex="name"
            title="Group"
            render={(value: string, record) => (
              <span>
                <span style={{ fontFamily: brand.fSans, fontWeight: 700, color: brand.ink }}>{value}</span>
                {record.tagline && (
                  <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11.5, color: brand.sub }}>
                    {record.tagline}
                  </div>
                )}
              </span>
            )}
          />
          <Table.Column<ProposalRow>
            dataIndex="proposer_name"
            title="Proposed by"
            render={(_: unknown, record) => (
              <Space size={6}>
                {renderAvatarName(record.proposer_name)}
                {record.proposer_class_year && <Pill tone="neutral">{`’${record.proposer_class_year.slice(-2)}`}</Pill>}
              </Space>
            )}
          />
          <Table.Column<ProposalRow>
            title="Category · privacy"
            dataIndex="category"
            render={(_: unknown, record) => (
              <Space size={4} wrap>
                <Pill tone="jaguar">{categoryLabel(record.category)}</Pill>
                <Pill tone="neutral">{privacyLabel(record.privacy)}</Pill>
              </Space>
            )}
          />
          <Table.Column<ProposalRow>
            title="Checks"
            dataIndex="checks"
            render={(_: unknown, record) => {
              const coverOk = record.cover_moderation_status === 'approved'
              const sameYear =
                record.class_year_min && record.class_year_min === record.class_year_max
                  ? record.class_year_min
                  : null
              return (
                <Space size={4} wrap>
                  <Pill tone={coverOk ? 'green' : 'gold'}>{coverOk ? 'Cover OK' : 'Cover needs review'}</Pill>
                  {record.similar && <Pill tone="gold">{`Similar: ${record.similar.name}`}</Pill>}
                  {sameYear && <Pill tone="neutral">{`Limit to class ’${sameYear.slice(-2)}?`}</Pill>}
                </Space>
              )
            }}
          />
          <Table.Column<ProposalRow>
            title="Actions"
            dataIndex="actions"
            render={(_: unknown, record) => (
              <Space>
                <Button
                  type="primary"
                  size="small"
                  data-testid={`proposal-approve-${record.id}`}
                  loading={busyId === record.id}
                  disabled={busyId !== null}
                  onClick={() => void approve(record)}
                >
                  Approve
                </Button>
                <Button
                  danger
                  size="small"
                  data-testid={`proposal-decline-${record.id}`}
                  disabled={busyId !== null}
                  onClick={() => setDeclineTarget(record)}
                >
                  Decline
                </Button>
              </Space>
            )}
          />
        </Table>
      </TableSurface>

      <Modal
        title="Why isn’t this approved?"
        open={declineTarget !== null}
        onCancel={() => {
          setDeclineTarget(null)
          declineForm.resetFields()
        }}
        okText="Decline proposal"
        okButtonProps={{ danger: true, loading: busyId === declineTarget?.id }}
        onOk={() => {
          declineForm
            .validateFields()
            .then((values) => {
              if (declineTarget) void decline(declineTarget, values.note)
            })
            .catch(() => {})
        }}
      >
        <Form form={declineForm} layout="vertical">
          <Form.Item
            name="note"
            label="Note to the proposer"
            extra="This note is sent to the proposer."
            rules={[{ required: true, message: 'A note is required.' }]}
          >
            <Input.TextArea rows={3} maxLength={500} data-testid="proposal-decline-note" />
          </Form.Item>
        </Form>
      </Modal>
    </ListPage>
  )
}
